import { applyAtPrefix } from '../proposer/proposerFeature.js';
import { updateGenerationRecord, deleteGenerationRecord, reorderGenerations, generateKnockout } from '../../services/apiClient.js';
import { resolveStaticSvg } from '../../utils/knockoutRecord.js';
import { extractKnockoutTarget, formatKnockoutDisplayText } from '../../utils/knockoutInput.js';

export function createDashboardCardFeature({
  dashboardItems,
  dashboardList,
  exportService,
  normalizeFilename,
  loadFont,
  generateSVG,
  parseFont,
  prepareAnimLine,
  getAnimFont,
  setAnimFont,
  resizeDashMiniCanvas,
  startDashMiniAnim,
  stopDashMiniAnim,
  updateDashCount,
  rebuildGalerie,
  updateCarteMarkers,
}) {
  let dragSrcId        = null;
  const dashCardTpl    = document.getElementById('dashboardCardTpl');

  async function dashExportSVG(item) {
    const buf = await loadFont();
    const svg = await resolveStaticSvg({
      script: item.script, text: item.text, knockoutSvg: item.knockoutSvg,
      generateSVG, fontBuffer: buf, color: '#000000',
    });
    exportService.downloadSVG(svg, normalizeFilename(item.text) + '.svg');
  }

  async function dashExportPNG(item) {
    const buf = await loadFont();
    const svg = await resolveStaticSvg({
      script: item.script, text: item.text, knockoutSvg: item.knockoutSvg,
      generateSVG, fontBuffer: buf, color: '#000000',
    });
    await exportService.downloadPNG(svg, normalizeFilename(item.text) + '.png');
  }

  async function dashExportPeripage(item, btn, statusEl) {
    await exportService.downloadPeripagePng({
      lineData: item.data,
      filename: `${normalizeFilename(item.text) || 'punchline'}-peripage-5cm-300dpi.png`,
      statusEl,
      btn,
    });
  }

  async function updateDashItem(item, newText) {
    newText = newText.trim();
    if (!newText || newText === item.text) return;
    try {
      const buf = await loadFont();
      if (!getAnimFont()) setAnimFont(await parseFont(buf));
      const lineData = await prepareAnimLine(newText, getAnimFont());
      if (!lineData) return;
      stopDashMiniAnim(item);
      item.text = newText;
      item.data = lineData;
      item.tX   = 0;
      item.rX   = 0;
      item.cardEl.querySelector('.dash-label').textContent = newText;
      resizeDashMiniCanvas(item);
      startDashMiniAnim(item);
      rebuildGalerie();
      if (item.id.startsWith('db-')) {
        const numId = parseInt(item.id.slice(3), 10);
        updateGenerationRecord(numId, { inputText: newText })
          .catch(err => console.error('Erreur mise à jour DB:', err));
      }
    } catch (err) {
      console.error('Update dash item error:', err);
    }
  }

  // Edition d'une punchline non-latine : le texte tape peut reporter le
  // marqueur de knockout ("*mot*" -- cf. extractKnockoutTarget), optionnel
  // (comme le latin sans MAJUSCULES : pas de marqueur = pas de rectangle
  // knockout). Contrairement au latin, regenerer relance le pipeline
  // serveur complet (/api/knockout, 15-90s) -- retourne true si la carte a
  // bien ete mise a jour (statusEl peut alors etre efface par l'appelant),
  // false sinon (message d'erreur laisse affiche dans statusEl).
  async function updateDashItemServerScript(item, rawText, statusEl) {
    const trimmed = rawText.trim();
    if (!trimmed) {
      statusEl.textContent = 'Erreur : le texte ne peut pas être vide.';
      return false;
    }
    const extracted = extractKnockoutTarget(trimmed);
    const sentence = extracted ? extracted.sentence : trimmed;
    const target = extracted ? extracted.target : null;
    if (sentence === item.text && target === item.target) return true;

    try {
      const result = await generateKnockout({
        script: item.script, sentence, target,
      });
      stopDashMiniAnim(item);
      item.text = sentence;
      item.target = target;
      item.knockoutSvg = result.svg;
      // pathParts (un contour par lettre) prioritaire sur pathData -- cf.
      // knockoutRecord.js/lineEngine.js pour le detail (25 septembre 2026).
      {
        const { pathData, pathParts, ...rest } = result.lineData;
        const parts = pathParts ?? [pathData];
        item.data = { p2ds: parts.map((d) => new Path2D(d)), ...rest };
      }
      item.tX = 0;
      item.rX = 0;
      item.cardEl.querySelector('.dash-label').textContent = formatKnockoutDisplayText(item.text, item.target);
      resizeDashMiniCanvas(item);
      startDashMiniAnim(item);
      rebuildGalerie();

      if (item.id.startsWith('db-')) {
        const numId = parseInt(item.id.slice(3), 10);
        await updateGenerationRecord(numId, {
          inputText: sentence,
          target,
          knockoutSvg: result.svg,
          knockoutLineData: result.lineData,
        });
      }
      return true;
    } catch (err) {
      console.error('Update dash item (non-latin) error:', err);
      statusEl.textContent = `Erreur : ${err.message}`;
      return false;
    }
  }

  function deleteDashItem(id) {
    const idx = dashboardItems.findIndex(it => it.id === id);
    if (idx === -1) return;
    const item = dashboardItems[idx];
    stopDashMiniAnim(item);
    if (item.ro) { item.ro.disconnect(); item.ro = null; }
    item.cardEl.remove();
    dashboardItems.splice(idx, 1);
    updateDashCount();
    rebuildGalerie();
    updateCarteMarkers();
    if (id.startsWith('db-')) {
      const numId = parseInt(id.slice(3), 10);
      deleteGenerationRecord(numId)
        .catch(err => console.error('Erreur suppression DB:', err));
    }
  }

  function createDashCard(item) {
    const frag = dashCardTpl.content.cloneNode(true);
    const li   = frag.querySelector('.dash-card');

    li.querySelector('.dash-label').textContent = item.script && item.script !== 'Latn'
      ? formatKnockoutDisplayText(item.text, item.target)
      : item.text;

    const miniWrap = li.querySelector('.dash-anim-mini');
    const canvas   = document.createElement('canvas');
    canvas.style.display = 'block';
    miniWrap.appendChild(canvas);
    item.canvas = canvas;
    item.ctx    = canvas.getContext('2d');
    item.tX     = 0;
    item.rX     = 0;

    const btnSvg        = li.querySelector('.dash-btn--svg');
    const btnPng        = li.querySelector('.dash-btn--png');
    const btnMp4        = li.querySelector('.dash-btn--mp4');
    const btnMp4Dark    = li.querySelector('.dash-btn--mp4-dark');
    const btnPeripage   = li.querySelector('.dash-btn--peripage');
    const btnDel        = li.querySelector('.dash-btn--delete');
    const btnEdit       = li.querySelector('.dash-btn--edit');
    const labelEl       = li.querySelector('.dash-label');
    const statusEl      = li.querySelector('.dash-export-status');
    const pseudoRowEl   = li.querySelector('.dash-pseudo-row');
    const pseudoEl      = li.querySelector('.dash-pseudo');
    const btnEditPseudo = li.querySelector('.dash-btn--edit-pseudo');

    function savePseudoDB(val) {
      if (!item.id.startsWith('db-')) return;
      const numId = parseInt(item.id.slice(3), 10);
      updateGenerationRecord(numId, { pseudo: val })
        .catch(err => console.error('Erreur mise à jour pseudo:', err));
    }

    function showPseudoText() {
      pseudoEl.textContent = item.pseudo;
      pseudoEl.hidden      = false;
      btnEditPseudo.hidden = false;
    }

    function showPseudoInput() {
      pseudoEl.hidden      = true;
      btnEditPseudo.hidden = true;
      const inp = document.createElement('input');
      inp.type           = 'text';
      inp.className      = 'dash-pseudo-input';
      inp.placeholder    = 'Ton pseudo Instagram (ou autre)';
      inp.autocapitalize = 'none';
      pseudoRowEl.insertBefore(inp, pseudoEl);
      inp.addEventListener('input', () => applyAtPrefix(inp));

      let committed = false;
      function commit() {
        if (committed) return;
        committed    = true;
        const val    = inp.value.trim();
        inp.remove();
        item.pseudo  = val;
        if (val) showPseudoText(); else showPseudoInput();
        savePseudoDB(val);
      }
      function cancel() {
        if (committed) return;
        committed = true;
        inp.remove();
        if (item.pseudo) showPseudoText(); else showPseudoInput();
      }
      inp.addEventListener('blur', commit);
      inp.addEventListener('keydown', e => {
        if (e.key === 'Enter')  { e.preventDefault(); inp.blur(); }
        if (e.key === 'Escape') { e.preventDefault(); inp.removeEventListener('blur', commit); cancel(); }
      });
    }

    if (item.pseudo) showPseudoText(); else showPseudoInput();

    btnEditPseudo.addEventListener('click', () => {
      pseudoEl.hidden      = true;
      btnEditPseudo.hidden = true;
      showPseudoInput();
    });

    btnEdit.addEventListener('click', () => {
      const isServerScript = item.script && item.script !== 'Latn';
      const editInput        = document.createElement('input');
      editInput.type         = 'text';
      editInput.value        = isServerScript
        ? formatKnockoutDisplayText(item.text, item.target)
        : item.text;
      editInput.className    = 'dash-label-input';
      editInput.autocapitalize = 'none';
      labelEl.hidden      = true;
      btnEdit.hidden      = true;
      labelEl.parentElement.insertBefore(editInput, labelEl);
      editInput.focus();
      editInput.select();

      let committed = false;
      async function commit() {
        if (committed) return;
        committed = true;
        const val = editInput.value;

        if (!isServerScript) {
          editInput.remove();
          labelEl.hidden = false;
          btnEdit.hidden = false;
          updateDashItem(item, val);
          return;
        }

        // Ecriture non-latine : la regeneration serveur prend 15-90s (cf.
        // /api/knockout) -- l'input reste affiche (desactive) avec un statut,
        // plutot que de rebasculer tout de suite sur le label (qui montrerait
        // encore l'ancien mot cible pendant toute l'attente). En cas d'echec
        // (marqueur manquant, erreur serveur...), on rend la main plutot que
        // de perdre la saisie du moderateur -- il corrige et reessaie.
        editInput.disabled = true;
        statusEl.textContent = '⏳ génération en cours (jusqu\'à 90s)…';
        const restored = await updateDashItemServerScript(item, val, statusEl);
        if (restored) {
          editInput.remove();
          labelEl.hidden = false;
          btnEdit.hidden = false;
          setTimeout(() => { statusEl.textContent = ''; }, 4000);
        } else {
          editInput.disabled = false;
          editInput.focus();
          committed = false;
        }
      }
      function cancel() {
        if (committed) return;
        committed = true;
        editInput.remove();
        labelEl.hidden = false;
        btnEdit.hidden = false;
      }
      editInput.addEventListener('blur', commit);
      editInput.addEventListener('keydown', e => {
        if (e.key === 'Enter')  { e.preventDefault(); editInput.blur(); }
        if (e.key === 'Escape') { e.preventDefault(); editInput.removeEventListener('blur', commit); cancel(); }
      });
    });

    btnSvg.addEventListener('click', () => dashExportSVG(item));
    btnPng.addEventListener('click', () => dashExportPNG(item));
    btnPeripage.addEventListener('click', () => {
      void dashExportPeripage(item, btnPeripage, statusEl);
    });
    btnMp4.addEventListener('click', () => exportService.doExportMP4({
      lineData:    item.data,
      canvasLiveW: item.canvas ? (item.canvas.clientWidth || 220) : 220,
      filename:    normalizeFilename(item.text) || 'nocode-animation',
      bg: '#ffffff', fg: '#000000', suffix: '', dir: 1,
      statusEl, btn: btnMp4, otherBtn: btnMp4Dark,
    }));
    btnMp4Dark.addEventListener('click', () => exportService.doExportMP4({
      lineData:    item.data,
      canvasLiveW: item.canvas ? (item.canvas.clientWidth || 220) : 220,
      filename:    normalizeFilename(item.text) || 'nocode-animation',
      bg: '#000000', fg: '#ffffff', suffix: '-noir', dir: -1,
      statusEl, btn: btnMp4Dark, otherBtn: btnMp4,
    }));
    btnDel.addEventListener('click', () => {
      li.classList.add('dash-card--removing');
      li.addEventListener('animationend', () => deleteDashItem(item.id), { once: true });
    });

    li.addEventListener('dragstart', e => {
      dragSrcId = item.id;
      li.classList.add('dash-card--dragging');
      e.dataTransfer.effectAllowed = 'move';
    });
    li.addEventListener('dragend', () => {
      li.classList.remove('dash-card--dragging');
      dashboardList.querySelectorAll('.dash-card').forEach(c => c.classList.remove('dash-card--over'));
    });
    li.addEventListener('dragover', e => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      dashboardList.querySelectorAll('.dash-card').forEach(c => c.classList.remove('dash-card--over'));
      if (dragSrcId !== item.id) li.classList.add('dash-card--over');
    });
    li.addEventListener('drop', e => {
      e.preventDefault();
      if (dragSrcId === item.id) return;
      const srcIdx = dashboardItems.findIndex(it => it.id === dragSrcId);
      const dstIdx = dashboardItems.findIndex(it => it.id === item.id);
      if (srcIdx === -1 || dstIdx === -1) return;
      const [moved] = dashboardItems.splice(srcIdx, 1);
      dashboardItems.splice(dstIdx, 0, moved);
      const srcEl = dashboardList.querySelector(`[data-id="${dragSrcId}"]`);
      if (srcIdx < dstIdx) li.after(srcEl); else li.before(srcEl);
      li.classList.remove('dash-card--over');
      rebuildGalerie();
      const dbIds = dashboardItems
        .filter(it => it.id.startsWith('db-'))
        .map(it => parseInt(it.id.slice(3), 10));
      if (dbIds.length > 0) {
        reorderGenerations(dbIds).catch(err => console.error('Erreur réordonnancement:', err));
      }
    });

    li.dataset.id = item.id;
    item.cardEl   = li;
    return li;
  }

  return { createDashCard, deleteDashItem, updateDashItem };
}
